import React, { memo } from 'react';
import Img from '@wconcept/wconcept-ui-renewal/src/components/atoms/Img';
import Text from '@wconcept/wconcept-ui-renewal/src/components/atoms/Text';
import Button from '@wconcept/wconcept-ui-renewal/src/components/atoms/Button';
import ItemsSwiper from '@wconcept/wconcept-ui-renewal/src/components/atoms/ItemsSwiper';
import {
  IconArrowRight05,
  IconArrowRight14,
} from '@wconcept/wconcept-ui-renewal/components/atoms/svg';
import SwiperNavigation from '@wconcept/wconcept-ui-renewal/components/molecules/SwiperNavigation';
import BasicBtn from '@wconcept/wconcept-ui-renewal/components/molecules/_btn/BasicBtn';
import {
  classNameBind,
  getUniqueKey,
} from '@wconcept/wconcept-common/utils/commonUtils';
import * as S from './style';

interface IProps {
  imgSrc?: string | string[];
  title?: string;
  desc?: string;
  isDesktop?: boolean;
  colorType?: 'white' | undefined | string;
  onClickBanner?: () => void;
  onClickAllPrd?: () => void;
  productList?: React.ReactNode;
}

const PC_PRODUCTS_PER_PAGE = 4;

const chunkArray = <T,>(arr: T[], size: number): T[][] => {
  const result: T[][] = [];
  for (let i = 0; i < arr.length; i += size) {
    result.push(arr.slice(i, i + size));
  }
  return result;
};

const HighlightPickItem2 = ({
  imgSrc,
  title,
  desc,
  isDesktop,
  colorType = 'black',
  onClickBanner,
  onClickAllPrd,
  productList,
}: IProps) => {
  const cName = classNameBind(['highlight-pick-item item']);
  const productNaviKey = `highlight-pick-product-navi-${getUniqueKey()}`;

  // 타이틀·서브타이틀이 모두 없으면 텍스트 영역을 두지 않고 배너 이동 화살표만 이미지 위에 노출한다.
  const hasInfoText = Boolean(title && desc);
  const showAreaInfo = hasInfoText || Boolean(onClickBanner);
  const areaInfoCName = classNameBind([
    'area-info',
    !hasInfoText ? 'no-text' : '',
  ]);

  // PC에서는 상품 리스트를 세로 리스트형(type list)으로 노출
  const productArray = React.Children.toArray(productList).map(child =>
    isDesktop && React.isValidElement(child)
      ? React.cloneElement(child as React.ReactElement<{ type?: string }>, {
          type: 'list',
        })
      : child
  );
  const isShowAllPrdBtn = productArray.length > 3;

  const renderProductList = () => {
    if (isDesktop) {
      const productPages = chunkArray(productArray, PC_PRODUCTS_PER_PAGE);
      const showNavi = productPages.length > 1;

      return (
        <div className="product-list-wrap">
          <ItemsSwiper
            className="product-list-swiper"
            loop={false}
            simulateTouch={false}
            allowTouchMove={false}
            navigation={
              showNavi
                ? {
                    prevEl: `.${productNaviKey} .swiper-button-prev`,
                    nextEl: `.${productNaviKey} .swiper-button-next`,
                  }
                : false
            }
            navigationEl={
              showNavi ? (
                <SwiperNavigation
                  type={colorType === 'black' ? 'white' : 'black'}
                  className={productNaviKey}
                  offsetTop={185}
                />
              ) : undefined
            }
          >
            {productPages.map((page, i) => (
              // eslint-disable-next-line react/no-array-index-key
              <React.Fragment key={i}>
                <div className="product-list">{page}</div>
              </React.Fragment>
            ))}
          </ItemsSwiper>
        </div>
      );
    }

    // 모바일: 최대 3개만 노출, 4개 이상이면 전체보기 버튼 노출
    const displayedProducts = isShowAllPrdBtn
      ? productArray.slice(0, 3)
      : productArray;

    return (
      <div className="product-list-wrap">
        <div className="product-list">{displayedProducts}</div>
        {isShowAllPrdBtn && (
          <div className="btn-more-wrap">
            <BasicBtn
              className="btn-more"
              text={`상품 ${productArray.length}개 전체보기`}
              type="lineDropdown"
              onClick={onClickAllPrd}
            />
          </div>
        )}
      </div>
    );
  };

  return (
    <S.HighlightPickItem2 className={cName}>
      <div className="img-banner">
        {Array.isArray(imgSrc) ? (
          <ItemsSwiper
            className="highlight-swiper"
            pagination={{ type: 'bullets' }}
            loop={imgSrc.length > 1}
            simulateTouch={!isDesktop}
            allowTouchMove={!isDesktop}
            // 부모(섹션) Swiper와 같은 방향의 중첩 Swiper. 터치 이벤트를 먼저 잡아
            // 부모로 전파되지 않게 한다.
            nested
          >
            {imgSrc.map((src, i) => (
              // eslint-disable-next-line react/no-array-index-key
              <Img key={i} src={src} alt={`${title} - ${i}`} />
            ))}
          </ItemsSwiper>
        ) : (
          <Img src={imgSrc} alt={title} />
        )}
        {showAreaInfo && (
          <div className={areaInfoCName}>
            {hasInfoText && (
              <div className="text-wrap">
                {title && (
                  <Text className="title" block>
                    {title}
                  </Text>
                )}
                {desc && (
                  <Text className="desc" block>
                    {desc}
                  </Text>
                )}
              </div>
            )}
            {onClickBanner && (
              <Button className="icon-arrow" onClick={onClickBanner}>
                {isDesktop || !hasInfoText ? (
                  <IconArrowRight14 />
                ) : (
                  <IconArrowRight05 />
                )}
              </Button>
            )}
          </div>
        )}
      </div>
      {renderProductList()}
    </S.HighlightPickItem2>
  );
};

export default memo(HighlightPickItem2);
